import React, { useState } from 'react';
// UI
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
// Context
import { useAuth } from 'AuthContext';
// Services
import { updateUserProfile } from 'services/users/users.service';
// Utils
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
  buildTermsAcceptanceSettings,
  hasAcceptedCurrentTerms,
} from 'utils/legalLinks';

/**
 * Blocking dialog for signed-in users who have not accepted the current Terms version.
 */
const TermsAcceptanceDialog = () => {
  const { user, userProfile, setUserProfile, loading: authLoading } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const needsAcceptance =
    Boolean(user) &&
    Boolean(userProfile) &&
    !hasAcceptedCurrentTerms(userProfile.settings);

  const handleAccept = async () => {
    if (!user || !agreed) return;
    try {
      setSubmitting(true);
      setError(null);
      const updated = await updateUserProfile(user.uid, {
        settings: buildTermsAcceptanceSettings(),
      });
      setUserProfile(updated);
    } catch (err) {
      console.error('Failed to save terms acceptance:', err);
      setError(err.message || 'Failed to save acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !needsAcceptance) {
    return null;
  }

  return (
    <Dialog open disableEscapeKeyDown>
      <DialogTitle>Terms of Use</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Conscious Book Club has zero tolerance for objectionable content and abusive
          behavior. Please review and accept our Terms of Use and Privacy Policy to continue.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Link href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
              .
            </Typography>
          }
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!agreed || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {submitting ? 'Saving…' : 'Accept and continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceDialog;
